import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { encounterAPI, intakeAPI, aiAPI, billingAPI, visitSummaryAPI } from '../../lib/api';
import api from '../../lib/api';
import { format } from 'date-fns';
import { 
  Robot, 
  Sparkle,
  Check,
  Warning,
  ClipboardText,
  Heart,
  Pill,
  MagnifyingGlass,
  X,
  Plus,
  PaperPlaneTilt,
  Storefront,
  Stethoscope,
  CaretDown,
  Microphone,
  Stop,
  Envelope,
  FileText,
  CheckCircle,
  Printer,
  UserCircle,
  CalendarCheck,
  ArrowRight,
  CalendarBlank
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Calendar } from '../../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';

// Extracted modal components
import { PrescriptionModal, AVSModal, CMACheckoutModal } from './components';

// Common pharmacy chains for searching
const PHARMACIES_DATABASE = [
  { id: 'ph1', name: 'CVS Pharmacy', address: '123 Main St', phone: '(555) 123-4567', fax: '(555) 123-4568' },
  { id: 'ph2', name: 'Walgreens', address: '456 Oak Ave', phone: '(555) 234-5678', fax: '(555) 234-5679' },
  { id: 'ph3', name: 'Rite Aid', address: '789 Pine Blvd', phone: '(555) 345-6789', fax: '(555) 345-6790' },
  { id: 'ph4', name: 'Walmart Pharmacy', address: '321 Commerce Dr', phone: '(555) 456-7890', fax: '(555) 456-7891' },
  { id: 'ph5', name: 'Target Pharmacy', address: '654 Market St', phone: '(555) 567-8901', fax: '(555) 567-8902' },
  { id: 'ph6', name: 'Costco Pharmacy', address: '987 Wholesale Way', phone: '(555) 678-9012', fax: '(555) 678-9013' },
  { id: 'ph7', name: 'Kroger Pharmacy', address: '147 Grocery Ln', phone: '(555) 789-0123', fax: '(555) 789-0124' },
  { id: 'ph8', name: 'Publix Pharmacy', address: '258 Shopping Ctr', phone: '(555) 890-1234', fax: '(555) 890-1235' },
  { id: 'ph9', name: 'Safeway Pharmacy', address: '369 Super St', phone: '(555) 901-2345', fax: '(555) 901-2346' },
  { id: 'ph10', name: 'H-E-B Pharmacy', address: '480 Texas Ave', phone: '(555) 012-3456', fax: '(555) 012-3457' },
];

export default function EncounterPage() {
  const { encounterId } = useParams();
  const navigate = useNavigate();
  const [encounter, setEncounter] = useState(null);
  const [intake, setIntake] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scribeLoading, setScribeLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Diagnosis state
  const [selectedDiagnoses, setSelectedDiagnoses] = useState([]);
  const [diagnosisSearch, setDiagnosisSearch] = useState('');
  const [diagnosisResults, setDiagnosisResults] = useState([]);
  const [diagnosisLoading, setDiagnosisLoading] = useState(false);
  const [showDiagnosisDropdown, setShowDiagnosisDropdown] = useState(false);
  const [aiSuggestedDiagnoses, setAiSuggestedDiagnoses] = useState([]);
  const [aiSuggestLoading, setAiSuggestLoading] = useState(false);
  
  // Treatment & Notes
  const [treatmentPlan, setTreatmentPlan] = useState('');
  const [conversationText, setConversationText] = useState('');
  const [scribeNotes, setScribeNotes] = useState('');
  
  // Prescription state
  const [prescriptions, setPrescriptions] = useState([]);
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  
  // Pharmacy state
  const [pharmacySearch, setPharmacySearch] = useState('');
  const [pharmacyResults, setPharmacyResults] = useState([]);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [showPharmacyDropdown, setShowPharmacyDropdown] = useState(false);

  // Voice transcription state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);

  // After Visit Summary state
  const [showAVSModal, setShowAVSModal] = useState(false);
  const [avsLoading, setAvsLoading] = useState(false);
  const [avsSent, setAvsSent] = useState(false);
  const [avsSummaryId, setAvsSummaryId] = useState(null);
  const [avsError, setAvsError] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpInstructions, setFollowUpInstructions] = useState('');

  // CMA Checkout state
  const [showCMACheckoutModal, setShowCMACheckoutModal] = useState(false);
  const [cmaCheckoutLoading, setCmaCheckoutLoading] = useState(false);
  const [cmaCheckoutSent, setCmaCheckoutSent] = useState(false);
  const [cmaInstructions, setCmaInstructions] = useState({
    return_visit: '',
    scheduling_needs: '',
    pharmacy_verification: '',
    medication_followup: '',
    additional_notes: ''
  });

  const loadEncounter = useCallback(async () => {
    try {
      const res = await encounterAPI.getById(encounterId);
      setEncounter(res.data);
      
      // Load existing data
      if (res.data.diagnoses && Array.isArray(res.data.diagnoses) && res.data.diagnoses.length > 0) {
        // Filter out any entries with 'LEGACY' pseudo-code
        const validDiagnoses = res.data.diagnoses.filter(d => 
          d.code !== 'LEGACY' && !d.description?.startsWith('LEGACY:')
        );
        setSelectedDiagnoses(validDiagnoses.length > 0 ? validDiagnoses : res.data.diagnoses);
      } else if (res.data.diagnosis) {
        // Legacy: convert old string diagnosis to new format by parsing each line
        const diagStr = res.data.diagnosis;
        if (typeof diagStr === 'string' && diagStr.trim()) {
          const parsedDiagnoses = diagStr.split('\n')
            .map(line => line.trim())
            .filter(line => line.length > 0 && !line.startsWith('LEGACY:'))
            .map(line => {
              // Remove any existing LEGACY prefix
              const cleanLine = line.replace(/^LEGACY:\s*/gi, '');
              // Parse "CODE: Description" format
              const colonIndex = cleanLine.indexOf(':');
              if (colonIndex > 0 && colonIndex < 15) {
                const code = cleanLine.substring(0, colonIndex).trim();
                // Don't use 'LEGACY' as a code
                if (code.toUpperCase() === 'LEGACY') {
                  return { code: '', description: cleanLine.substring(colonIndex + 1).trim() };
                }
                return {
                  code: code,
                  description: cleanLine.substring(colonIndex + 1).trim()
                };
              }
              return { code: '', description: cleanLine };
            });
          setSelectedDiagnoses(parsedDiagnoses);
        }
      }
      if (res.data.treatment_plan) setTreatmentPlan(res.data.treatment_plan);
      if (res.data.ai_scribe_notes) setScribeNotes(res.data.ai_scribe_notes);
      if (res.data.prescriptions) setPrescriptions(res.data.prescriptions);
      if (res.data.pharmacy) setSelectedPharmacy(res.data.pharmacy);
      
      // Load intake data
      if (res.data.intake_id) {
        const intakeRes = await intakeAPI.getById(res.data.intake_id);
        setIntake(intakeRes.data);
      }
    } catch (err) {
      setError('Failed to load encounter');
    } finally {
      setLoading(false);
    }
  }, [encounterId]); // encounterAPI and intakeAPI are module-level imports

  useEffect(() => {
    loadEncounter();
  }, [loadEncounter]);

  // Search ICD-10 codes
  useEffect(() => {
    const searchDiagnosis = async () => {
      if (diagnosisSearch.length < 2) {
        setDiagnosisResults([]);
        return;
      }
      
      setDiagnosisLoading(true);
      try {
        const res = await billingAPI.getICD10Codes(diagnosisSearch);
        setDiagnosisResults(res.data.codes || []);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          process.env.NODE_ENV === "development" && console.error('Error searching diagnoses:', err);
        }
      } finally {
        setDiagnosisLoading(false);
      }
    };
    
    const debounce = setTimeout(searchDiagnosis, 300);
    return () => clearTimeout(debounce);
  }, [diagnosisSearch]);

  // Search pharmacies
  useEffect(() => {
    if (pharmacySearch.length < 2) {
      setPharmacyResults([]);
      return;
    }
    
    const searchLower = pharmacySearch.toLowerCase();
    const results = PHARMACIES_DATABASE.filter(ph => 
      ph.name.toLowerCase().includes(searchLower) ||
      ph.address.toLowerCase().includes(searchLower)
    );
    setPharmacyResults(results);
  }, [pharmacySearch]);

  // Get AI diagnosis suggestions
  const getAiDiagnosisSuggestions = async () => {
    if (aiSuggestLoading) return; // Prevent double-clicks
    
    if (!intake?.chief_complaint && !scribeNotes) {
      toast.error('No clinical data available for AI suggestions');
      return;
    }
    
    setAiSuggestLoading(true);
    try {
      const encounterText = `
        Chief Complaint: ${intake?.chief_complaint || 'N/A'}
        Symptoms: ${intake?.symptoms?.join(', ') || 'N/A'}
        Notes: ${scribeNotes || conversationText || 'N/A'}
      `;
      
      const res = await billingAPI.aiSuggestCodes({ encounter_notes: encounterText });
      if (res.data.diagnosis_codes && res.data.diagnosis_codes.length > 0) {
        setAiSuggestedDiagnoses(res.data.diagnosis_codes);
        toast.success('AI suggestions loaded');
      } else {
        toast.info('No diagnosis suggestions found');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to get AI suggestions';
      if (errorMsg.toLowerCase().includes('budget') || errorMsg.toLowerCase().includes('quota')) {
        toast.error('AI service budget exceeded. Please add balance to your Emergent LLM Key (Profile → Manage Plan → Universal Key → Add Balance).');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setAiSuggestLoading(false);
    }
  };

  const addDiagnosis = (diagnosis) => {
    if (!selectedDiagnoses.find(d => d.code === diagnosis.code)) {
      setSelectedDiagnoses([...selectedDiagnoses, diagnosis]);
    }
    setDiagnosisSearch('');
    setShowDiagnosisDropdown(false);
  };

  const removeDiagnosis = (code) => {
    setSelectedDiagnoses(selectedDiagnoses.filter(d => d.code !== code));
  };

  const runAiScribe = async () => {
    if (!conversationText.trim()) return;
    
    setScribeLoading(true);
    try {
      const res = await aiAPI.scribe({
        encounter_id: encounterId,
        conversation_text: conversationText,
      });
      setScribeNotes(res.data.scribe_notes);
      toast.success('SOAP notes generated');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'AI scribe failed';
      if (errorMsg.toLowerCase().includes('budget')) {
        toast.error('AI service budget exceeded. Add balance: Profile → Manage Plan → Universal Key');
        setError('AI service budget exceeded. Go to Profile → Manage Plan → Universal Key → Add Balance.');
      } else {
        toast.error('Could not generate notes. Please try again.');
      }
    } finally {
      setScribeLoading(false);
    }
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Check supported mime types
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm'
        : 'audio/mp4';
      
      const recorder = new MediaRecorder(stream, { mimeType });
      const audioChunks = [];
      
      recorder.ondataavailable = (e) => {
        console.log('Audio chunk received:', e.data.size, 'bytes');
        if (e.data.size > 0) {
          audioChunks.push(e.data);
        }
      };
      
      recorder.onstop = async () => {
        toast.info('Stop handler triggered...');
        console.log('Recording stopped. Chunks collected:', audioChunks.length);
        stream.getTracks().forEach(track => track.stop());
        
        if (audioChunks.length === 0) {
          toast.error('No audio was captured. Please check your microphone.');
          return;
        }
        
        const blob = new Blob(audioChunks, { type: mimeType });
        toast.info(`Audio captured: ${Math.round(blob.size/1024)}KB`);
        console.log('Audio blob size:', blob.size, 'bytes');
        
        if (blob.size < 1000) {
          toast.error('Recording too short or no audio detected. Please try again.');
          return;
        }
        
        // Convert blob to base64 and send to transcription API
        setIsTranscribing(true);
        toast.info('Processing audio...');
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64Audio = reader.result.split(',')[1]; // Remove data URL prefix
          try {
            const res = await aiAPI.transcribe({
              audio_data: base64Audio,
              encounter_id: encounterId,
              generate_notes: true
            });
            
            if (res.data.transcription) {
              setConversationText(prev => prev + (prev ? '\n\n' : '') + res.data.transcription);
              toast.success('Audio transcribed successfully');
            } else {
              toast.warning('No speech detected in recording');
            }
            if (res.data.clinical_notes) {
              setScribeNotes(res.data.clinical_notes);
            }
          } catch (err) {
            const errorMsg = err.response?.data?.detail || 'Transcription failed';
            if (errorMsg.toLowerCase().includes('budget')) {
              toast.error('AI service budget exceeded. Add balance: Profile → Manage Plan → Universal Key');
            } else {
              // Show friendly message instead of raw technical details
              toast.error('Could not transcribe the recording. Please try again.');
            }
          } finally {
            setIsTranscribing(false);
          }
        };
        reader.onerror = () => {
          toast.error('Failed to process audio file');
          setIsTranscribing(false);
        };
        reader.readAsDataURL(blob);
      };
      
      // Start recording with timeslice to ensure data is captured
      recorder.start(1000); // Capture data every 1 second
      setMediaRecorder(recorder);
      setIsRecording(true);
      toast.success('Recording started - speak clearly into your microphone');
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Microphone access denied. Please allow microphone in your browser settings.');
      } else if (err.name === 'NotFoundError') {
        toast.error('No microphone found. Please connect a microphone.');
      } else {
        toast.error('Could not access microphone: ' + err.message);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setIsRecording(false);
      setMediaRecorder(null);
    }
  };

  // Called by PrescriptionModal when adding a prescription
  const addPrescription = (newRx) => {
    if (newRx) {
      // From external modal component
      setPrescriptions([...prescriptions, { ...newRx, status: 'pending' }]);
      toast.success('Prescription added');
    }
  };

  const removePrescription = (rxId) => {
    setPrescriptions(prescriptions.filter(rx => rx.id !== rxId));
  };

  const sendToPharmacy = async () => {
    if (!selectedPharmacy) {
      toast.error('Please select a pharmacy first');
      return;
    }
    if (prescriptions.length === 0) {
      toast.error('No prescriptions to send');
      return;
    }
    
    // Simulate e-prescribe
    toast.success(`Prescriptions sent to ${selectedPharmacy.name}`);
    setPrescriptions(prescriptions.map(rx => ({ ...rx, status: 'sent' })));
  };

  const saveEncounter = async (complete = false) => {
    setSaving(true);
    try {
      const diagnosisText = selectedDiagnoses.map(d => `${d.code}: ${d.description}`).join('\n');
      
      const updateData = {
        diagnosis: diagnosisText,
        diagnoses: selectedDiagnoses,
        treatment_plan: treatmentPlan,
        prescriptions,
        pharmacy: selectedPharmacy,
        status: complete ? 'completed' : 'in_progress',
      };
      
      await encounterAPI.update(encounterId, updateData);
      
      if (complete) {
        toast.success('Encounter completed');
        // Show After Visit Summary modal
        setShowAVSModal(true);
      } else {
        toast.success('Progress saved');
      }
    } catch (err) {
      setError('Failed to save encounter');
    } finally {
      setSaving(false);
    }
  };

  const sendAfterVisitSummary = async () => {
    if (!encounter?.patient_id) {
      toast.error('Patient information not available');
      return;
    }
    
    setAvsLoading(true);
    setAvsError('');
    try {
      // Call API with follow-up parameters
      const res = await visitSummaryAPI.send(
        encounter.patient_id, 
        encounterId,
        followUpDate || null,
        followUpInstructions || null
      );
      
      if (res.data?.success === false && !res.data?.summary_id) {
        // Actual failure
        setAvsError(res.data?.message || 'Failed to send After Visit Summary');
        toast.error(res.data?.message || 'Failed to send');
      } else if (res.data?.summary_id) {
        // Success - summary was created (email may be in demo mode)
        setAvsSummaryId(res.data.summary_id);
        setAvsSent(true);
        if (res.data?.success === false) {
          // Summary saved but email not sent (demo mode)
          toast.info('Summary saved to records (email in demo mode)');
        } else {
          toast.success('After Visit Summary sent to patient');
        }
      } else {
        setAvsSummaryId(res.data?.summary_id);
        setAvsSent(true);
        toast.success('After Visit Summary processed');
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        process.env.NODE_ENV === "development" && console.error('AVS error:', err);
      }
      const errorMsg = err.response?.data?.detail || 'Failed to send After Visit Summary';
      setAvsError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setAvsLoading(false);
    }
  };

  const closeAVSAndNavigate = () => {
    setShowAVSModal(false);
    navigate('/provider');
  };

  // Send patient to CMA Checkout Queue
  const sendToCMACheckout = async () => {
    if (!encounter?.patient_id) {
      toast.error('Patient information not available');
      return;
    }

    setCmaCheckoutLoading(true);
    try {
      await api.post('/checkout/send-to-cma', {
        patient_id: encounter.patient_id,
        encounter_id: encounterId,
        instructions: cmaInstructions
      });
      
      setCmaCheckoutSent(true);
      toast.success('Patient sent to CMA checkout queue');
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('CMA checkout error:', err);
      }
      const errorMsg = err.response?.data?.detail || 'Failed to send to CMA checkout';
      toast.error(errorMsg);
    } finally {
      setCmaCheckoutLoading(false);
    }
  };

  const closeCMACheckoutAndNavigate = () => {
    setShowCMACheckoutModal(false);
    navigate('/provider');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!encounter) {
    return (
      <div className="p-8 text-center">
        <Warning size={48} className="text-red-500 mx-auto mb-4" />
        <p className="text-slate-700">Encounter not found</p>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="encounter-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Patient Encounter
        </h1>
        <p className="text-slate-500 mt-1">{encounter.patient_name} - {encounter.encounter_type?.replace(/_/g, ' ')}</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Patient Summary */}
          {intake && (
            <div className="card-clinical p-6" data-testid="patient-summary">
              <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <ClipboardText size={18} /> Patient Summary
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-2">Chief Complaint</h4>
                  <p className="text-slate-900">{intake.chief_complaint}</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-2">Symptoms</h4>
                  <div className="flex flex-wrap gap-1">
                    {intake.symptoms?.map((s) => (
                      <span key={`symptom-${s}`} className="px-2 py-0.5 bg-slate-100 rounded text-sm">{s}</span>
                    ))}
                  </div>
                </div>
                
                {intake.vital_signs && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-500 mb-2 flex items-center gap-1">
                      <Heart size={14} /> Vital Signs
                    </h4>
                    <div className="text-sm space-y-1">
                      {intake.vital_signs.blood_pressure && <p>BP: {intake.vital_signs.blood_pressure}</p>}
                      {intake.vital_signs.heart_rate && <p>HR: {intake.vital_signs.heart_rate} bpm</p>}
                      {intake.vital_signs.temperature && <p>Temp: {intake.vital_signs.temperature}°F</p>}
                      {intake.vital_signs.oxygen_saturation && <p>O2: {intake.vital_signs.oxygen_saturation}%</p>}
                    </div>
                  </div>
                )}
                
                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-2">Pain Level</h4>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-slate-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          intake.pain_level >= 7 ? 'bg-red-500' :
                          intake.pain_level >= 4 ? 'bg-amber-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${intake.pain_level * 10}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium">{intake.pain_level}/10</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Diagnosis Section */}
          <div className="card-clinical p-6" data-testid="diagnosis-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Stethoscope size={18} /> Diagnosis (ICD-10-CM)
              </h2>
              <button
                onClick={getAiDiagnosisSuggestions}
                disabled={aiSuggestLoading}
                className={`text-sm flex items-center gap-1 ${aiSuggestLoading ? 'text-slate-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-700'}`}
                data-testid="ai-suggest-btn"
              >
                {aiSuggestLoading ? (
                  <>
                    <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <Sparkle size={14} /> AI Suggest
                  </>
                )}
              </button>
            </div>
            
            {/* Search Input */}
            <div className="relative mb-4">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={diagnosisSearch}
                onChange={(e) => {
                  setDiagnosisSearch(e.target.value);
                  setShowDiagnosisDropdown(true);
                }}
                onFocus={() => setShowDiagnosisDropdown(true)}
                placeholder="Search ICD-10 codes (e.g., 'diabetes', 'J06', 'hypertension')..."
                className="input-clinical pl-10 w-full"
                data-testid="diagnosis-search"
              />
              
              {/* Dropdown Results */}
              {showDiagnosisDropdown && (diagnosisResults.length > 0 || diagnosisLoading) && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {diagnosisLoading ? (
                    <div className="p-4 text-center text-slate-500">
                      <div className="spinner inline-block mr-2" /> Searching...
                    </div>
                  ) : (
                    diagnosisResults.map((diag) => (
                      <button
                        key={diag.code}
                        onClick={() => addDiagnosis(diag)}
                        className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                      >
                        <span className="font-mono text-teal-600 font-medium">{diag.code}</span>
                        <span className="text-slate-600 ml-2">{diag.description}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            
            {/* AI Suggestions */}
            {aiSuggestedDiagnoses.length > 0 && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-blue-700 mb-2 flex items-center gap-1">
                  <Sparkle size={14} /> AI Suggested Diagnoses
                </h4>
                <div className="space-y-2">
                  {aiSuggestedDiagnoses.map((diag, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border border-blue-200">
                      <div>
                        <span className="font-mono text-blue-600">{diag.code}</span>
                        <span className="text-slate-600 ml-2 text-sm">{diag.description}</span>
                        {diag.rationale && (
                          <p className="text-xs text-slate-500 mt-1">{diag.rationale}</p>
                        )}
                      </div>
                      <button
                        onClick={() => addDiagnosis(diag)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <Plus size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Selected Diagnoses */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-slate-500">Selected Diagnoses</h4>
              {selectedDiagnoses.length === 0 ? (
                <p className="text-slate-400 text-sm italic">No diagnoses selected. Search above to add ICD-10 codes.</p>
              ) : (
                selectedDiagnoses.map((diag, idx) => (
                  <div 
                    key={diag.code} 
                    className="flex items-center justify-between p-3 bg-teal-50 rounded-lg border border-teal-200"
                    data-testid={`selected-diagnosis-${idx}`}
                  >
                    <div>
                      <span className="font-mono text-teal-700 font-semibold">{diag.code}</span>
                      <span className="text-slate-700 ml-2">{diag.description}</span>
                    </div>
                    <button
                      onClick={() => removeDiagnosis(diag.code)}
                      className="text-red-500 hover:text-red-600"
                    >
                      <X size={18} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Prescriptions Section */}
          <div className="card-clinical p-6" data-testid="prescriptions-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Pill size={18} /> Prescriptions (E-Prescribe)
              </h2>
              <button
                onClick={() => setShowPrescriptionModal(true)}
                className="btn-teal text-sm py-2"
                data-testid="add-rx-btn"
              >
                <Plus size={16} className="mr-1" /> Add Medication
              </button>
            </div>

            {/* Pharmacy Selection */}
            <div className="mb-4 p-4 bg-slate-50 rounded-lg">
              <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                <Storefront size={16} /> Patient&apos;s Pharmacy
              </h4>
              
              {selectedPharmacy ? (
                <div className="flex items-center justify-between bg-white p-3 rounded border">
                  <div>
                    <p className="font-medium">{selectedPharmacy.name}</p>
                    <p className="text-sm text-slate-500">{selectedPharmacy.address}</p>
                    <p className="text-sm text-slate-500">Tel: {selectedPharmacy.phone}</p>
                  </div>
                  <button
                    onClick={() => setSelectedPharmacy(null)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X size={18} />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={pharmacySearch}
                    onChange={(e) => {
                      setPharmacySearch(e.target.value);
                      setShowPharmacyDropdown(true);
                    }}
                    onFocus={() => setShowPharmacyDropdown(true)}
                    placeholder="Search pharmacy by name or address..."
                    className="input-clinical pl-10 w-full text-sm"
                    data-testid="pharmacy-search"
                  />
                  
                  {showPharmacyDropdown && pharmacyResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {pharmacyResults.map((ph) => (
                        <button
                          key={ph.id}
                          onClick={() => {
                            setSelectedPharmacy(ph);
                            setPharmacySearch('');
                            setShowPharmacyDropdown(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                        >
                          <p className="font-medium text-sm">{ph.name}</p>
                          <p className="text-xs text-slate-500">{ph.address}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Prescriptions List */}
            {prescriptions.length === 0 ? (
              <p className="text-slate-400 text-sm italic text-center py-4">
                No prescriptions added. Click &quot;Add Medication&quot; to prescribe.
              </p>
            ) : (
              <div className="space-y-3">
                {prescriptions.map((rx) => (
                  <div 
                    key={rx.id} 
                    className={`p-4 rounded-lg border ${rx.status === 'sent' ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'}`}
                    data-testid={`prescription-${rx.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-900">
                          {rx.medication.name} {rx.medication.strength}
                        </p>
                        <p className="text-sm text-slate-600">{rx.medication.form}</p>
                        <p className="text-sm text-slate-500 mt-1">
                          {rx.dosage} - {rx.frequency} for {rx.duration}
                        </p>
                        <p className="text-sm text-slate-500">
                          Qty: {rx.quantity} | Refills: {rx.refills}
                        </p>
                        {rx.instructions && (
                          <p className="text-sm text-slate-500 mt-1 italic">{rx.instructions}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {rx.status === 'sent' && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Sent</span>
                        )}
                        <button
                          onClick={() => removePrescription(rx.id)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Send to Pharmacy Button */}
                {prescriptions.some(rx => rx.status !== 'sent') && selectedPharmacy && (
                  <button
                    onClick={sendToPharmacy}
                    className="w-full btn-teal flex items-center justify-center gap-2"
                    data-testid="send-to-pharmacy-btn"
                  >
                    <PaperPlaneTilt size={18} />
                    Send to {selectedPharmacy.name}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Treatment Plan */}
          <div className="card-clinical p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Treatment Plan & Notes</h2>
            <textarea
              value={treatmentPlan}
              onChange={(e) => setTreatmentPlan(e.target.value)}
              className="input-clinical min-h-[120px]"
              placeholder="Enter treatment plan, follow-up instructions, patient education..."
              data-testid="treatment-input"
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* AI Scribe */}
          <div className="card-clinical p-6 ai-feature-card" data-testid="ai-scribe-panel">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Robot size={20} className="text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">AI Scribe</h3>
                <p className="text-xs text-slate-500">Auto-generate SOAP notes</p>
              </div>
            </div>
            
            {/* Voice Recording */}
            <div className="mb-4 flex gap-2">
              <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isTranscribing}
                className={`flex-1 py-2 px-4 rounded-lg flex items-center justify-center gap-2 text-sm font-medium ${
                  isTranscribing
                    ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                    : isRecording 
                    ? 'bg-red-100 text-red-700 border border-red-300' 
                    : 'bg-slate-100 text-slate-700 border border-slate-300 hover:bg-slate-200'
                }`}
                data-testid="voice-record-btn"
              >
                {isTranscribing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" /> Processing...
                  </>
                ) : isRecording ? (
                  <>
                    <Stop size={16} weight="fill" /> Stop Recording
                  </>
                ) : (
                  <>
                    <Microphone size={16} /> Voice Input
                  </>
                )}
              </button>
            </div>
            
            <div className="mb-4">
              <label className="label-clinical">Clinical Conversation</label>
              <textarea
                value={conversationText}
                onChange={(e) => setConversationText(e.target.value)}
                className="input-clinical min-h-[120px] text-sm"
                placeholder="Type or paste clinical conversation here..."
                data-testid="conversation-input"
              />
            </div>
            
            <button
              onClick={runAiScribe}
              disabled={scribeLoading || !conversationText.trim()}
              className="w-full btn-teal mb-4 flex items-center justify-center gap-2"
              data-testid="run-ai-scribe"
            >
              {scribeLoading ? (
                <>
                  <div className="spinner" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkle size={18} weight="fill" />
                  Generate SOAP Notes
                </>
              )}
            </button>
            
            {scribeNotes && (
              <div className="ai-tracing">
                <label className="label-clinical">Generated Notes</label>
                <div className="p-4 bg-slate-50 rounded-lg text-sm text-slate-700 leading-relaxed max-h-48 overflow-y-auto whitespace-pre-wrap">
                  {scribeNotes}
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="card-clinical p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Actions</h3>
            <div className="space-y-3">
              <button
                onClick={() => saveEncounter(false)}
                disabled={saving}
                className="w-full btn-outline flex items-center justify-center gap-2"
                data-testid="save-encounter"
              >
                {saving ? <div className="spinner" /> : 'Save Progress'}
              </button>
              <button
                onClick={() => saveEncounter(true)}
                disabled={saving}
                className="w-full btn-teal flex items-center justify-center gap-2"
                data-testid="complete-encounter"
              >
                <Check size={18} weight="bold" />
                Complete Encounter
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Extracted Modal Components */}
      <PrescriptionModal
        isOpen={showPrescriptionModal}
        onClose={() => setShowPrescriptionModal(false)}
        onAddPrescription={addPrescription}
      />

      <AVSModal
        isOpen={showAVSModal}
        onClose={closeAVSAndNavigate}
        onSendAVS={sendAfterVisitSummary}
        onSendToCMA={() => {
          setShowAVSModal(false);
          setShowCMACheckoutModal(true);
        }}
        encounter={encounter}
        intake={intake}
        selectedDiagnoses={selectedDiagnoses}
        treatmentPlan={treatmentPlan}
        prescriptions={prescriptions}
        selectedPharmacy={selectedPharmacy}
        followUpDate={followUpDate}
        setFollowUpDate={setFollowUpDate}
        followUpInstructions={followUpInstructions}
        setFollowUpInstructions={setFollowUpInstructions}
        avsLoading={avsLoading}
        avsSent={avsSent}
        avsSummaryId={avsSummaryId}
        avsError={avsError}
      />

      <CMACheckoutModal
        isOpen={showCMACheckoutModal}
        onClose={closeCMACheckoutAndNavigate}
        onBack={() => {
          setShowCMACheckoutModal(false);
          setShowAVSModal(true);
        }}
        onSend={sendToCMACheckout}
        cmaInstructions={cmaInstructions}
        setCmaInstructions={setCmaInstructions}
        cmaCheckoutLoading={cmaCheckoutLoading}
        cmaCheckoutSent={cmaCheckoutSent}
      />
    </div>
  );
}
